import React, { useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

export default function HeroSlider({ slides }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [selected, setSelected] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !slides || slides.length < 2) return;
    intervalRef.current = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [emblaApi, slides]);

  if (!slides || slides.length === 0) return null;

  return (
    <div className="relative w-[calc(100%-1.5rem)] sm:w-full mx-3 sm:mx-0 mt-3 sm:mt-0 mb-4 sm:mb-0 rounded-3xl sm:rounded-none h-[42vh] sm:h-[78vh] min-h-[280px] sm:min-h-[520px] max-h-[420px] sm:max-h-[760px] overflow-hidden shadow-xl sm:shadow-none" data-testid="hero-slider">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((s, i) => (
            <div key={s.id || i} className="relative flex-[0_0_100%] h-full" data-testid={`hero-slide-${i}`}>
              <img
                src={s.image_url}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-950/80 via-blue-950/55 to-blue-950/30"/>
              <div className="absolute inset-0 max-w-7xl mx-auto px-4 sm:px-10 flex items-center">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.16em]">
                    Morar Bem · Cashback
                  </span>
                  <h1 className="font-display text-2xl sm:text-5xl lg:text-6xl font-bold text-white mt-2.5 sm:mt-5 leading-[1.1] sm:leading-[1.05] drop-shadow-md">
                    {s.title}
                  </h1>
                  {s.subtitle && <p className="text-white/90 text-xs sm:text-lg mt-2 sm:mt-4 leading-relaxed max-w-xl line-clamp-3 sm:line-clamp-none">{s.subtitle}</p>}
                  {s.cta_text && s.cta_link && (
                    <Link to={s.cta_link}>
                      <Button size="sm" className="mt-3 sm:mt-7 bg-white hover:bg-stone-100 text-blue-950 font-semibold rounded-xl sm:rounded-2xl h-9 sm:h-12 px-3.5 sm:px-6 text-xs sm:text-base shadow-xl" data-testid={`hero-cta-${i}`}>
                        {s.cta_text} <ArrowRight size={14} className="ml-1.5 sm:ml-2"/>
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={() => emblaApi && emblaApi.scrollPrev()} aria-label="Anterior"
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur text-white items-center justify-center transition-colors z-10"
            data-testid="slider-prev"><ChevronLeft/></button>
          <button onClick={() => emblaApi && emblaApi.scrollNext()} aria-label="Próximo"
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur text-white items-center justify-center transition-colors z-10"
            data-testid="slider-next"><ChevronRight/></button>
          <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${selected === i ? "w-10 bg-white" : "w-2 bg-white/50"}`}
                data-testid={`slider-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
